import { assertUnreachable, isDefined } from '@sgme/fp';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { FormSelect } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FormSelect.tsx';
import { SolveRateGroup } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/SolvingGroup/SolveRateGroup.tsx';
import { SolvingUpfrontField } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardMainFields/SolvingGroup/SolvingUpfrontField.tsx';
import { useCancelFlexiForwardRfsMutation } from '@/store/flexi-forward/api';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import { solvingTypeChanged } from '@/store/flexi-forward/slice.ts';
import { DEFAULT_SOLVING_TYPE, POSSIBLE_SOLVING_TYPE, type SolvingType } from '@/store/flexi-forward/state.ts';
import { useAppDispatch } from '@/store/hooks.ts';

export function SolvingGroup({ tileId }: { tileId: string }): ReactElement {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { selectedSolvingType, rfsId } = useFlexiForwardSelector((flexiForward) => {
    const rfsState = flexiForward.properties.rfs;

    return {
      selectedSolvingType: flexiForward.properties.solvingType ?? DEFAULT_SOLVING_TYPE,
      rfsId: isDefined(rfsState) && 'rfsId' in rfsState ? rfsState.rfsId : undefined,
    };
  });

  function formatSolvingTypeOptions(value: SolvingType): string {
    return formatMessage({ id: `flexiForwardTile.mainFields.solvingType.option.${value}` });
  }

  const [cancelFlexiForwardRfs] = useCancelFlexiForwardRfsMutation();
  const handleSolvingTypeChanged = useFlexiForwardPropertyChangedHandler<SolvingType>(
    tileId,
    'Solving type change',
    (solvingType) => {
      dispatch(solvingTypeChanged({ tileId, solvingType }));

      if (isDefined(rfsId)) {
        cancelFlexiForwardRfs({
          tileId,
          rfsId,
        });
      }

      const upfront = solvingType === 'zeroCost' ? 0 : null;
      const rate = solvingType !== 'zeroCost' ? null : undefined;

      return { upfront, rate };
    },
  );

  return (
    <>
      <FlexiForwardFormField className="pt-16px">
        <FormSelect<SolvingType>
          values={POSSIBLE_SOLVING_TYPE}
          selectedValue={selectedSolvingType ?? DEFAULT_SOLVING_TYPE}
          onChange={handleSolvingTypeChanged}
          formatValue={formatSolvingTypeOptions}
          data-e2e="solving-premium-select-selector"
        />
      </FlexiForwardFormField>

      {renderSolvingContent(selectedSolvingType, tileId)}
    </>
  );
}

function renderSolvingContent(solvingType: SolvingType, tileId: string): ReactElement | null {
  switch (solvingType) {
    case 'zeroCost':
      return null;
    case 'solveRate':
      return <SolveRateGroup tileId={tileId} />;
    case 'solveUpfront':
      return <SolvingUpfrontField tileId={tileId} />;
    default:
      return assertUnreachable(solvingType, `Unknown solvingType: ${solvingType}`);
  }
}
